/**
 * SandboxedPreview — render untrusted HTML inside an iframe with the
 * tightest sandbox: only `allow-scripts` + `allow-same-origin`.
 *
 * Explicitly NO:
 *   - allow-forms          (landing pages don't POST anywhere)
 *   - allow-top-navigation (cannot redirect the parent owner-hub)
 *   - allow-popups
 *   - allow-modals
 *
 * The iframe document is populated via srcDoc, which is safer than a
 * data: URL for large strings and keeps the browser's same-origin
 * boundary clean.
 */

import { useEffect, useRef, useState } from 'react'

interface SandboxedPreviewProps {
  /** Full HTML document to render. */
  html: string
  /** Optional override label for screen readers. */
  title?: string
  /** Tailwind class applied to the host wrapper. */
  className?: string
  /** Height in px or CSS string; default 480. */
  height?: number | string
  /** Fired when the iframe finishes loading its srcDoc. */
  onLoad?: () => void
}

export function SandboxedPreview({
  html,
  title = 'Landing preview',
  className = '',
  height = 480,
  onLoad,
}: SandboxedPreviewProps) {
  const ref = useRef<HTMLIFrameElement>(null)
  const [loadedAt, setLoadedAt] = useState(0)

  useEffect(() => {
    // Trigger a re-render on srcDoc swap so tests can observe a stable
    // data-load-count attribute.
    setLoadedAt(Date.now())
  }, [html])

  const resolvedHeight = typeof height === 'number' ? `${height}px` : height

  return (
    <iframe
      ref={ref}
      title={title}
      // Critical: allow-scripts for countdown JS + allow-same-origin so
      // document.querySelectorAll works. NOTHING else. Do NOT add
      // allow-forms, allow-top-navigation, or allow-popups.
      sandbox="allow-scripts allow-same-origin"
      srcDoc={html}
      onLoad={() => {
        setLoadedAt(Date.now())
        onLoad?.()
      }}
      data-testid="sandboxed-preview"
      data-load-count={loadedAt}
      className={`w-full rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 ${className}`}
      style={{ height: resolvedHeight }}
    />
  )
}
